import type {
  SectionValue,
  MissedOpportunitiesSection as MissedOpportunitiesData,
} from "../../../../shared/analysis-types";
import { SectionHeader } from "./section-header";
import { NarrativeCard } from "./narrative-card";
import { SourceQuoteBlock } from "./source-quote-block";
import { LabeledBody } from "./labeled-body";
import { SectionErrorFallback } from "./section-error-fallback";
import { CopyFormattedButton } from "@/components/copy-formatted-button";
import {
  formatMissedOpportunitiesCopy,
  formatMissedOpportunityCopy,
} from "@/lib/analysis-copy-format";

interface MissedOpportunitiesSectionProps {
  data: SectionValue<MissedOpportunitiesData>;
  className?: string;
}

export function MissedOpportunitiesSection({
  data,
  className,
}: MissedOpportunitiesSectionProps) {
  if ("error" in data) {
    return (
      <div className={className}>
        <SectionHeader title="Missed Opportunities" />
        <div className="mt-4">
          <SectionErrorFallback sectionName="missed_opportunities" />
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <SectionHeader
        title="Missed Opportunities"
        count={data.opportunities.length}
        action={
          <CopyFormattedButton format={formatMissedOpportunitiesCopy(data)}>
            Copy section
          </CopyFormattedButton>
        }
      />
      <div className="mt-4 flex flex-col gap-6">
        {data.opportunities.map((opp, i) => (
          <NarrativeCard key={i} className="group relative pt-14 sm:pt-5 sm:pr-32">
            <CopyFormattedButton
              format={formatMissedOpportunityCopy(opp)}
              className="absolute right-5 top-5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
            >
              Copy item
            </CopyFormattedButton>
            <SourceQuoteBlock
              quote={opp.client_quote}
              label="CLIENT QUOTE"
            />

            <LabeledBody label="What happened" variant="descriptive">
              {opp.what_happened}
            </LabeledBody>

            <LabeledBody label="What to try" variant="action">
              {opp.what_to_try}
            </LabeledBody>

            <LabeledBody label="Why it matters" variant="descriptive">
              {opp.why_it_matters}
            </LabeledBody>
          </NarrativeCard>
        ))}
      </div>
    </div>
  );
}
